import { Button } from '@ftthnms/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Modal confirmation on the native <dialog> (focus trap and Esc from the browser).
 * `onConfirm` may throw: its message is shown and the dialog stays open.
 */
export function ConfirmDialog({
  title,
  icon,
  children,
  confirmLabel,
  tone = 'danger',
  onConfirm,
  onClose,
}: {
  title: string;
  icon?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="fn-dialog" onClose={onClose} aria-labelledby="confirm-title">
      <div
        className="fn-panel"
        style={{
          background: 'var(--bg-surface)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon && <span className={`fn-tile fn-tile--${tone}`}>{icon}</span>}
          <h2 id="confirm-title" className="fn-card__title" style={{ fontSize: 15 }}>
            {title}
          </h2>
        </div>
        {children && (
          <div className="fn-card__desc" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {children}
          </div>
        )}
        {error && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => ref.current?.close()} disabled={busy}>
            Batal
          </Button>
          <Button variant={tone} onClick={() => void confirm()} loading={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
